import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { alto, apple, bondiBlue, boulder, catskillWhite, fairPink, mineShaft, thunderBird, willowBrook } from '../Colors/Colors';
import { SIZE, LAYOUT } from './Constants';
import { ValidIcon, WarningIcon, ValidationRequiredIcon } from '@parchment/icons';

const noop = () => {};

const InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Input__InputWrapper",
  componentId: "sc-4qo86f-0"
})(["display:grid;border-radius:inherit;height:", ""], props => {
  const height = {
    small: '40',
    medium: '50',
    large: '60'
  };
  return "".concat(height[props.size], "px;");
});
const StyledInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "Input__StyledInput",
  componentId: "sc-4qo86f-1"
})(["height:", " width:100%;-webkit-appearance:none;border:none;border-radius:", " box-shadow:inset 0 0 0 1px ", ";font-size:", " font-weight:600;color:", ";text-indent:16px;margin:-1px 0 0 -1px;::placeholder{font-weight:normal;color:", ";}:-ms-input-placeholder{font-weight:normal;color:", ";}::-ms-input-placeholder{font-weight:normal;color:", ";}&:focus{position:relative;outline:none;background-color:", ";box-shadow:0 0 5px 0px  ", ";width:calc(100% - 1px);}&.required{&.grid{text-indent:35px;}}&.valid{text-indent:40px;background-color:", ";color:", ";box-shadow:inset 50px 0 0 -43px  ", "", ";&:focus{box-shadow:0 0 6px 0  ", ",inset 50px 0 0 -43px ", ";}}&.invalid{text-indent:40px;color:", ";background-color:", ";box-shadow:inset 50px 0 0 -43px  ", "", ";&:focus{box-shadow:0 0 6px 0  ", ",inset 50px 0 0 -43px ", ";}}"], props => {
  const height = {
    small: '40',
    medium: '50',
    large: '60'
  };
  return "".concat(height[props.size], "px;");
}, props => {
  const borderRadius = {
    small: '4',
    medium: '5',
    large: '6'
  };
  return "".concat(borderRadius[props.size], "px;");
}, alto, props => {
  const fontSize = {
    small: '13',
    medium: '13',
    large: '16'
  };
  return "".concat(fontSize[props.size], "px;");
}, mineShaft, boulder, boulder, boulder, catskillWhite, bondiBlue, willowBrook, apple, apple, props => !props.gridContainer && ", inset 0 0 0 1px  ".concat(alto), apple, apple, thunderBird, fairPink, thunderBird, props => !props.gridContainer && ", inset 0 0 0 1px  ".concat(alto), thunderBird, thunderBird);
StyledInput.displayName = 'StyledInput';
const ValidationIcon = /*#__PURE__*/styled.div.withConfig({
  displayName: "Input__ValidationIcon",
  componentId: "sc-4qo86f-2"
})(["font-size:", " margin-top:", " margin-left:0;pointer-events:none;padding-top:15px;padding-left:15px;"], props => {
  const fontSize = {
    small: '13',
    medium: '13',
    large: '16'
  };
  return "".concat(fontSize[props.size], "px;");
}, props => {
  const marginTop = {
    small: '45',
    medium: '50',
    large: '55'
  };
  return "-".concat(marginTop[props.size], "px;");
});
ValidationIcon.displayName = 'ValidationIcon';

const handleValidate = function (e, props) {
  props.onValidate(e.target.value, props);
};

const handleChange = function (e, props) {
  if (!props.boundSource || !props.boundSource.setState) {
    return;
  }

  let newState = {};
  newState[props.stateKey] = e.target.value;
  props.boundSource.setState(newState);
};

export const Input = function (props) {
  const {
    className,
    valid,
    touched,
    layout,
    required,
    boundSource,
    stateKey,
    name
  } = props;
  const classNames = [props.size];
  let validationIcon;

  if (touched) {
    if (valid) {
      classNames.push('valid');
      validationIcon = /*#__PURE__*/React.createElement(ValidIcon, {
        color: apple
      });
    } else {
      classNames.push('invalid');
      validationIcon = /*#__PURE__*/React.createElement(WarningIcon, {
        color: thunderBird
      });
    }
  } else {
    if (required && layout === 'grid') {
      validationIcon = /*#__PURE__*/React.createElement(ValidationRequiredIcon, {
        color: thunderBird
      });
    }

    if (layout === 'grid') {
      classNames.push('grid');

      if (required) {
        classNames.push('required');
      }
    }
  }

  if (className) {
    classNames.push(className);
  }

  const _value = boundSource && boundSource.state && boundSource.state[stateKey]; // filters out known non-DOM props from the props before supplying them
  // to the paginator.
  // eslint-disable-next-line no-unused-vars


  const {
    onValidate,
    onValid,
    ...inputProps
  } = props;
  return /*#__PURE__*/React.createElement(InputWrapper, {
    size: props.size
  }, /*#__PURE__*/React.createElement(StyledInput, _extends({}, inputProps, {
    className: classNames.join(' '),
    name: name || stateKey,
    placeholder: props.placeholder,
    value: _value,
    onBlur: e => handleValidate(e, props),
    onChange: e => handleChange(e, props)
  })), /*#__PURE__*/React.createElement(ValidationIcon, {
    size: props.size
  }, validationIcon));
};
Input.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf([SIZE.small, SIZE.medium, SIZE.large]),
  valid: PropTypes.bool,
  touched: PropTypes.bool,
  layout: PropTypes.PropTypes.oneOf([LAYOUT.basic, LAYOUT.column, LAYOUT.grid]),
  required: PropTypes.bool,
  boundSource: PropTypes.object,
  stateKey: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onValidate: PropTypes.func,
  onValid: PropTypes.func
};
Input.defaultProps = {
  size: SIZE.medium,
  validationPatterns: [],
  onValid: noop
};